import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Paper, Stack, Typography } from "@mui/material";
import { AppContext } from "contexts/AppContext";
import LanguageSelector from "components/blocks/LanguageSelector/LanguageSelector";
import AuthPage from "modules/session/AuthPage";
import Translate from "utils/Translate";

const copySx = {
    my: "var(--auth-section-gap)",
    fontSize: "var(--auth-copy-size)",
    lineHeight: 1.6,
    whiteSpace: "pre-line",
    "& h1, & h2, & h3, & h4, & h5, & h6": { fontSize: "inherit", lineHeight: 1.5 },
};
const columnSx = {
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
};

const NewRegisterPage = ({ children, title, description }) => {
    const { brandData, styleGuide } = useContext(AppContext);
    const features = brandData?.features || {};
    const registration = features.registerUser || {};
    const logo = brandData?.brandLoginPageLogo || brandData?.brandLogo;

    return (
        <AuthPage contained>
            <Paper
                component="main"
                tabIndex={0}
                aria-labelledby="register-title"
                elevation={0}
                className="register-card auth-card"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 1080,
                    maxHeight: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    p: "var(--auth-card-padding)",
                    borderRadius: "28px",
                    bgcolor: "#fff",
                    boxShadow: "0 24px 70px rgba(30, 45, 64, 0.14)",
                    fontFamily: styleGuide.secondaryFontFamily,
                    "@media (max-width: 767px)": {
                        display: "block",
                        overflowY: "auto",
                        borderRadius: "22px",
                    },
                }}
            >
                <Stack component="header" direction="row" sx={{ flexShrink: 0, justifyContent: "space-between", alignItems: "center", gap: 2, mb: "var(--auth-section-gap)" }}>
                    <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0, gap: 2 }}>
                        {logo && <Box component="img" sx={{ maxWidth: "min(200px, 100%)", maxHeight: "var(--auth-logo-height)", objectFit: "contain" }} src={logo} alt={brandData?.brandName || "Brand Logo"} />}
                        <Typography component="h1" id="register-title" sx={{ m: 0, fontFamily: styleGuide.fontFamily, fontSize: "var(--auth-title-size)", lineHeight: 1.2, fontWeight: 600 }}>
                            <Translate translationKey="_CREATE_NEW_USER_ACCOUNT_">Opret ny bruger</Translate>
                        </Typography>
                    </Stack>
                    <LanguageSelector />
                </Stack>
                <Box sx={{
                    display: "grid",
                    minHeight: 0,
                    gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
                    gap: "var(--auth-column-gap)",
                    "@media (max-width: 767px)": { gridTemplateColumns: "minmax(0, 1fr)", gap: "var(--auth-section-gap)" },
                }}>
                    <Box component="section" tabIndex={0} className="register-card__form" aria-labelledby="register-title" sx={columnSx}>
                        <Link component={RouterLink} className="register-card__back" to="/login" sx={{ display: "inline-block", mb: 2, color: "#465567", fontFamily: "inherit", fontSize: 14 }}>
                            <Translate translationKey="_REGISTER_BACK_TO_LOGIN_">Tilbage til login</Translate>
                        </Link>
                        {children}
                    </Box>
                    <Box tabIndex={0} className="register-card__information" sx={{
                        borderRadius: "18px",
                        bgcolor: "#f5f7f9",
                        color: "#465567",
                        minHeight: 0,
                        maxHeight: "100%",
                        px: 1,
                        py: "var(--auth-panel-padding)",
                    }}>
                        <Box sx={{...columnSx, maxHeight: "100%", p: "var(--auth-panel-padding)" }}>
                            {title && (
                                <Typography component="h2" sx={{ fontFamily: "inherit", fontSize: "var(--auth-subtitle-size)", lineHeight: 1.3, color: "#26384a", fontWeight: 500 }}>
                                    <Translate renderHtml translationKey="_REGISTER_BANNER_HEADLINE_">{title}</Translate>
                                </Typography>
                            )}
                            {description && (
                                <Box sx={copySx}>
                                    <Translate renderHtml translationKey="_REGISTER_BANNER_CONTENT_">{description}</Translate>
                                </Box>
                            )}
                            {registration.header && <Typography variant="h3" sx={{ mt: 3, fontFamily: "inherit", fontSize: 18, lineHeight: 1.4, color: "#26384a", fontWeight: 500 }}>{registration.header}</Typography>}
                            {registration.description && (
                                <Box sx={copySx} dangerouslySetInnerHTML={{ __html: registration.description }} />
                            )}
                            <Stack sx={{ alignItems: "flex-start", gap: 1, mt: "var(--auth-section-gap)", pt: "var(--auth-panel-padding)", borderTop: "1px solid #dce2e8" }}>
                                <Typography component="h3" sx={{ fontFamily: "inherit", fontSize: 18, fontWeight: 600 }}>
                                    <Translate translationKey="_CONTACT_INFORMATION_" fallback="Kontakt information" />
                                </Typography>
                                {registration.contactPersonImageUrl && (
                                    <Box
                                        component="img"
                                        sx={{ width: 80, height: 80, my: 1, borderRadius: "50%", objectFit: "cover" }}
                                        src={registration.contactPersonImageUrl}
                                        alt={registration.contactPersonName || ""}
                                    />
                                )}
                                {registration.contactPersonName && <Typography sx={{ fontFamily: "inherit" }}>{registration.contactPersonName}</Typography>}
                                {brandData?.contactEmail && <Link color="inherit" sx={{ fontFamily: "inherit" }} href={`mailto:${brandData.contactEmail}`}>{brandData.contactEmail}</Link>}
                                {brandData?.contactPhone && <Link color="inherit" sx={{ fontFamily: "inherit" }} href={`tel:${brandData.contactPhone}`}>{brandData.contactPhone}</Link>}
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </AuthPage>
    );
};

export default NewRegisterPage;
